import { memo, useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Toast } from '../index'
import { getActionIcon } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { ActionIconSlot } from '@renderer/components/action-icon-slot'
import {
  useStagedActionsQuery,
  useInvokeStagedAction,
  useStagedActionUndo,
  useArchiveStagedActions,
  useStagedActionDerivations,
  useStagedActionStatus,
  isStagedActionUserEdited,
  type StagedAction,
} from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { useConnectionError, ReconnectButton } from '@renderer/overlay/Sidepanel/components/action-connection-error'
import { useOpenViewInSidepanel } from '@renderer/overlay/hooks/use-open-view-in-sidepanel'
import { toastManager } from '../toast-store'
import { Badge, Button, ButtonGroup, Cell } from '@highlight/ui'
import { Check } from 'lucide-react'
import { CopyIconButton } from '@renderer/overlay/components/copy-icon-button'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import {
  stagedActionEventProperties,
  trackStagedActionCopy,
} from '@renderer/overlay/Sidepanel/hooks/staged-actions/track-staged-action'
import { HighlightAppIcon } from '@renderer/components/Icons/HighlightAppIcon'
import { ActionContentPreview } from '@renderer/components/action-content-preview'
import { resolveContactAvatarSrc } from '@renderer/utils/contact-avatar'
import { isAskUserQuestionStagedAction, getQuestionChatTarget } from '@renderer/chat/integrations/ask-user-question'
import {
  askUserCardVariant,
  getAnswerableFraming,
  isAnswerableStagedAction,
} from '@renderer/chat/integrations/answerable'
import { AskUserBadge } from '@renderer/chat/integrations/ask-user-card'
import {
  isReportToUserStagedAction,
  getReportOriginSkill,
  OriginSkillIcon,
} from '@renderer/chat/integrations/report-to-user'

/** Base lifetime while actions still need a decision. */
export const ACTION_TOAST_DURATION_MS = 10000
/**
 * Once every remaining action has succeeded there's nothing left to decide —
 * linger just long enough to read the confirmation, then get out of the way.
 */
export const ACTION_TOAST_SUCCESS_DURATION_MS = 3000

interface ActionToastProps {
  actions: Array<StagedAction>
  toastId: string
}

type DismissAction = (actionId: string, options?: { trackToastDismiss?: boolean }) => void

const ActionToastCell = memo(function ActionToastCell({
  action,
  liveAction,
  onDismiss,
}: {
  action: StagedAction
  liveAction: StagedAction | undefined
  onDismiss: DismissAction
}) {
  const invokeStagedActionWithSource = useInvokeStagedAction()
  const { undoStagedAction } = useStagedActionUndo()
  const { rejectStagedAction } = useArchiveStagedActions()
  const { openViewInSidepanel } = useOpenViewInSidepanel()

  const displayAction = liveAction ?? action
  const { status, isInvoking: isInvokingThisWindow, isUndoing } = useStagedActionStatus(displayAction)
  const connectionError = useConnectionError(displayAction)

  const view = displayAction.view
  if (!view) return null

  // Reflect the derived invoking *status* (server-reported `lastInvokedAt` with
  // no output), not just this window's invoke flag. Otherwise an action invoked
  // from another session — or still in-flight after a reload — would render an
  // enabled Invoke button here, allowing a duplicate invoke.
  const isInvoking = status === 'invoking' || isInvokingThisWindow
  const actionHasError = status === 'error'
  const isPending = status === 'pending' || status === 'completed'
  // Green check in the corner once the action has gone out, whichever badge the row wears.
  const sentBadge = isPending ? (
    <div className="bg-success flex size-3 items-center justify-center rounded-full shadow-sm">
      <Check className="size-2 text-white" strokeWidth={3} />
    </div>
  ) : undefined
  const hasUndoError = status === 'undo-error'
  const hasOutput = !!displayAction.lastInvokedAt

  // Background AskUserQuestion / AskUserFeedback: answering happens in the
  // drawer card, not via the invoke machinery — route the primary button to the
  // drawer instead. Neither synthetic toolset has a hydrator to invoke.
  const isAnswerable = isAnswerableStagedAction(displayAction)
  // Narrower: only a question has a chat to open.
  const isQuestion = isAskUserQuestionStagedAction(displayAction)

  // report_to_user delivers a message rather than proposing work: there is
  // nothing to execute, so the invoke CTA gives way to copy and the toast is
  // badged with the originating skill's icon when attributed.
  const isReport = isReportToUserStagedAction(displayAction)
  const reportSkill = getReportOriginSkill(displayAction)

  const handleClick = () => {
    // A background AskUserQuestion lives inside its originating chat — open that
    // conversation (scrolled to the question) so the user can answer it inline
    // with full context. Fall back to the question drawer when there's no chat
    // to open (e.g. a non-chat background run).
    const chatTarget = isQuestion ? getQuestionChatTarget(displayAction) : null
    if (chatTarget) {
      openViewInSidepanel('chat', chatTarget)
    } else {
      openViewInSidepanel('action', {
        actionId: displayAction.id,
        title: isAnswerable ? (isQuestion ? 'Question' : 'Feedback') : (view.title ?? displayAction.title),
        kind: isAnswerable ? (isQuestion ? ('question' as const) : ('feedback' as const)) : undefined,
      })
    }
    onDismiss(displayAction.id)
    AmplitudeRenderer.trackEvent('Staged Action', {
      action: 'view',
      ...stagedActionEventProperties(displayAction),
      source: 'toast',
    })
  }

  const handleInvoke = () => {
    if (isAnswerable) {
      handleClick()
      return
    }
    if (view?.invokeLink) {
      window.open(view.invokeLink, '_blank')
    } else {
      invokeStagedActionWithSource(displayAction.id, { source: 'button' }).catch(console.error)
    }
    AmplitudeRenderer.trackEvent('Staged Action', {
      action: 'invoke',
      ...stagedActionEventProperties(displayAction),
      status: status,
      source: 'toast',
      wasEdited: isStagedActionUserEdited(displayAction),
    })
  }

  // Reject: archive the action and send a negative signal, then drop it from the toast. Suppress
  // the toast-dismissed event so an active rejection isn't also counted as a passive dismissal.
  const handleReject = () => {
    rejectStagedAction(displayAction.id).catch(console.error)
    onDismiss(displayAction.id, { trackToastDismiss: false })
    AmplitudeRenderer.trackEvent('Staged Action', {
      action: 'reject',
      ...stagedActionEventProperties(displayAction),
      source: 'toast',
    })
  }

  const handleUndo = () => {
    undoStagedAction(displayAction.id).catch(console.error)
    AmplitudeRenderer.trackEvent('Staged Action', {
      action: 'undo',
      ...stagedActionEventProperties(displayAction),
      status: status,
      source: 'toast',
    })
  }

  return (
    <Cell clickable onClick={handleClick} className="p-0 opacity-100 shadow-none after:hidden">
      <Cell.Header showChevron={false}>
        {isAnswerable ? (
          <AskUserBadge variant={askUserCardVariant(displayAction)} subIcon={sentBadge} />
        ) : (
          <ActionIconSlot
            view={displayAction.view}
            subIcon={
              sentBadge ?? (
                <div className="bg-foreground text-background flex size-3 shrink-0 items-center justify-center rounded shadow-md">
                  <HighlightAppIcon size={8} />
                </div>
              )
            }
          >
            {reportSkill ? <OriginSkillIcon skillName={reportSkill} /> : getActionIcon(displayAction)}
          </ActionIconSlot>
        )}
        <Cell.Title className="text-foreground line-clamp-3 text-sm">{view.title ?? displayAction.title}</Cell.Title>
      </Cell.Header>

      {isAnswerable ? (
        // Mirror the sidepanel cell: frame *why* it exists, not the options.
        <Cell.Content>
          <p className="text-foreground-secondary line-clamp-2 text-[13px] leading-snug">
            {getAnswerableFraming(displayAction)}
          </p>
        </Cell.Content>
      ) : (
        (view.tags.length > 0 || view.content) && (
          <Cell.Content>
            {view.tags.length > 0 && (
              <Cell.Metadata scrollable={false}>
                {view.tags.map((tag) => (
                  <Badge
                    key={tag.key}
                    left={tag.icon ? <img src={resolveContactAvatarSrc(tag.icon)} alt="" /> : undefined}
                  >
                    <span>{tag.value}</span>
                  </Badge>
                ))}
              </Cell.Metadata>
            )}
            {view.content && <ActionContentPreview content={view.content} attachments={view.attachments ?? []} clamp />}
          </Cell.Content>
        )
      )}

      <Cell.Footer>
        <div className="flex w-full min-w-0 items-center justify-between gap-2">
          <div className="text-foreground-placeholder flex min-w-0 flex-1 items-center gap-1 overflow-hidden text-[13px]">
            {(isPending || hasUndoError) && view.supportsUndo && (hasOutput || hasUndoError) && (
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  handleUndo()
                }}
                size="sm"
                variant="ghost"
                disabled={isUndoing}
                loading={isUndoing}
              >
                Undo
              </Button>
            )}
          </div>

          {!isPending &&
            !hasUndoError &&
            !isInvoking &&
            (connectionError ? (
              <ReconnectButton action={displayAction} stopPropagation />
            ) : (
              <div className="flex shrink-0 items-center gap-1.5">
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleReject()
                  }}
                  size="sm"
                  variant="outline"
                  disabled={isInvoking}
                >
                  Archive
                </Button>
                {isReport ? (
                  view.content && (
                    <CopyIconButton
                      size="sm"
                      value={view.content}
                      icon="content"
                      ariaLabel="Copy content"
                      stopPropagation
                      onCopy={() => trackStagedActionCopy(action, 'content', { source: 'toast' })}
                    />
                  )
                ) : (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleInvoke()
                    }}
                    size="sm"
                    variant="primary"
                    tone={actionHasError ? 'danger' : 'neutral'}
                    disabled={isInvoking}
                    loading={isInvoking}
                  >
                    {actionHasError ? 'Retry' : view.invokeLabel}
                  </Button>
                )}
              </div>
            ))}

          {isInvoking && (
            <Button size="sm" variant="primary" disabled loading>
              {view.invokeLabel}
            </Button>
          )}

          {(isPending || hasUndoError) && view.buttons.length > 0 && (
            <div className="flex shrink-0 items-center gap-2">
              {view.buttons.map((button) => (
                <ButtonGroup key={button.link} size="sm" variant="outline">
                  <CopyIconButton
                    value={button.link}
                    icon="link"
                    ariaLabel={`Copy link for ${button.label}`}
                    stopPropagation
                    onCopy={() => trackStagedActionCopy(action, 'link', { source: 'toast' })}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation()
                      window.open(button.link, '_blank')
                    }}
                  >
                    {button.label}
                  </Button>
                </ButtonGroup>
              ))}
            </div>
          )}
        </div>
      </Cell.Footer>
    </Cell>
  )
})

const CompactActionCell = memo(function CompactActionCell({
  action,
  liveAction,
  onDismiss,
}: {
  action: StagedAction
  liveAction: StagedAction | undefined
  onDismiss: DismissAction
}) {
  const { openViewInSidepanel } = useOpenViewInSidepanel()

  const displayAction = liveAction ?? action
  const { status } = useStagedActionStatus(displayAction)
  const view = displayAction.view
  if (!view) return null

  const isPending = status === 'pending' || status === 'completed'
  const sentBadge = isPending ? (
    <div className="bg-success flex size-2.5 items-center justify-center rounded-full shadow-sm">
      <Check className="size-1.5 text-white" strokeWidth={3} />
    </div>
  ) : undefined
  const isAnswerable = isAnswerableStagedAction(displayAction)
  const isQuestion = isAskUserQuestionStagedAction(displayAction)
  const reportSkill = getReportOriginSkill(displayAction)

  const handleClick = () => {
    // Mirror ActionToastCell: a background question opens its chat scrolled to
    // the question; everything else opens the action drawer.
    const chatTarget = isQuestion ? getQuestionChatTarget(displayAction) : null
    if (chatTarget) {
      openViewInSidepanel('chat', chatTarget)
    } else {
      openViewInSidepanel('action', {
        actionId: displayAction.id,
        title: isAnswerable ? (isQuestion ? 'Question' : 'Feedback') : (view.title ?? displayAction.title),
        kind: isAnswerable ? (isQuestion ? ('question' as const) : ('feedback' as const)) : undefined,
      })
    }
    onDismiss(displayAction.id)
    AmplitudeRenderer.trackEvent('Staged Action', {
      action: 'view',
      ...stagedActionEventProperties(displayAction),
      source: 'toast',
    })
  }

  return (
    <Cell size="compact" clickable onClick={handleClick} className="p-0 shadow-none after:hidden">
      <Cell.Header showChevron={false}>
        {isAnswerable ? (
          <AskUserBadge variant={askUserCardVariant(displayAction)} subIcon={sentBadge} />
        ) : (
          <ActionIconSlot
            view={displayAction.view}
            subIcon={
              sentBadge ?? (
                <div className="bg-foreground text-background flex size-2.5 shrink-0 items-center justify-center rounded-[3px] shadow-md">
                  <HighlightAppIcon size={6} />
                </div>
              )
            }
          >
            {reportSkill ? <OriginSkillIcon skillName={reportSkill} /> : getActionIcon(displayAction)}
          </ActionIconSlot>
        )}
        <Cell.Title className="text-foreground text-sm">{view.title ?? displayAction.title}</Cell.Title>
      </Cell.Header>
    </Cell>
  )
})

const MAX_VISIBLE_ACTIONS = 3

export function ActionToast({ actions, toastId }: ActionToastProps) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())
  const { allActions } = useStagedActionsQuery()
  const { openViewInSidepanel } = useOpenViewInSidepanel()

  const liveActionsById = useMemo(() => new Map(allActions.map((action) => [action.id, action])), [allActions])
  const hasLoadedRef = useRef(false)
  if (allActions.length > 0) hasLoadedRef.current = true

  // An archived row is gone as far as the toast is concerned: `dismissStagedAction`
  // keeps it in the cache (the list's undo window needs it), so presence alone
  // would leave a just-archived action on screen with live buttons. Dropping it
  // here is what dismisses the toast silently — the effect below closes it once
  // nothing is left.
  const remainingActions = useMemo(
    () =>
      hasLoadedRef.current
        ? actions.filter((a) => {
            const live = liveActionsById.get(a.id)
            return !dismissedIds.has(a.id) && !!live && live.archivedAt == null
          })
        : actions.filter((a) => !dismissedIds.has(a.id)),
    [actions, dismissedIds, liveActionsById],
  )

  useEffect(() => {
    if (remainingActions.length === 0 && hasLoadedRef.current) {
      toastManager.dismiss(toastId)
    }
  }, [remainingActions.length, toastId])

  // Shorten the toast once every remaining action has succeeded ('pending' /
  // 'completed' — the green-check state). Error and undo-error states keep the
  // full duration, and Undo restores it so the toast doesn't vanish mid-undo.
  const { getActionStatus } = useStagedActionDerivations()
  const allSucceeded =
    remainingActions.length > 0 &&
    remainingActions.every((a) => {
      const status = getActionStatus(liveActionsById.get(a.id) ?? a)
      return status === 'pending' || status === 'completed'
    })
  useEffect(() => {
    toastManager.setDuration(toastId, allSucceeded ? ACTION_TOAST_SUCCESS_DURATION_MS : ACTION_TOAST_DURATION_MS)
  }, [allSucceeded, toastId])

  const visibleActions = remainingActions.slice(0, MAX_VISIBLE_ACTIONS)
  const overflowCount = remainingActions.length - visibleActions.length

  const handleDismissAction = useCallback(
    (actionId: string, options?: { trackToastDismiss?: boolean }) => {
      const dismissed = actions.find((a) => a.id === actionId)
      if (dismissed && (options?.trackToastDismiss ?? true)) {
        AmplitudeRenderer.trackEvent('Staged Action', {
          action: 'toast dismissed',
          ...stagedActionEventProperties(dismissed),
        })
      }

      setDismissedIds((prev) => new Set(prev).add(actionId))

      const remaining = actions.filter((a) => a.id !== actionId && !dismissedIds.has(a.id))
      if (remaining.length === 0) {
        toastManager.dismiss(toastId)
      }
    },
    [actions, dismissedIds, toastId],
  )

  if (remainingActions.length === 0) return null

  const isCompact = remainingActions.length >= 2

  const handleViewActions = () => {
    openViewInSidepanel('actions')
    toastManager.dismiss(toastId)
  }

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        <div className="flex w-full flex-col gap-3">
          {visibleActions.map((action, i) => (
            <div
              key={action.id}
              className="animate-in fade-in slide-in-from-bottom-1 fill-mode-both duration-300"
              style={{ animationDelay: `${i * 75}ms` }}
            >
              {!isCompact && i > 0 && <div className="bg-element-hover -mt-1 mb-3 h-px w-full" />}
              {isCompact ? (
                <CompactActionCell
                  action={action}
                  liveAction={liveActionsById.get(action.id)}
                  onDismiss={handleDismissAction}
                />
              ) : (
                <ActionToastCell
                  action={action}
                  liveAction={liveActionsById.get(action.id)}
                  onDismiss={handleDismissAction}
                />
              )}
            </div>
          ))}
          {overflowCount > 0 && (
            <div className="text-foreground-placeholder text-center text-xs">
              +{overflowCount} more action{overflowCount > 1 ? 's' : ''}
            </div>
          )}
          {isCompact && (
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={handleViewActions}>
                View actions
              </Button>
            </div>
          )}
        </div>
      </Toast.Content>
    </Toast>
  )
}
